import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AiLogo } from './assistant/AiLogo';
import { useAssistantEnabled } from '../hooks/useAssistantEnabled';
import { INTELLIGENCE_PATH } from '../lib/assistantLinks';

export type AssistantMode = 'dashboard' | 'intelligence';

const DASHBOARD_PATH = '/dashboard';

interface AssistantModeSwitchProps {
  /** The page this switch sits on. */
  active: AssistantMode;
}

/**
 * Dashboard ⇄ TimeGlobe Intelligence, next to the page title of both pages.
 * Each side is a page of its own (`/dashboard`, `/intelligence`), so the
 * switch navigates. Only rendered where the assistant is switched on.
 */
const AssistantModeSwitch: React.FC<AssistantModeSwitchProps> = ({ active }) => {
  const { enabled } = useAssistantEnabled();
  const navigate = useNavigate();
  if (!enabled) return null;

  const options: { id: AssistantMode; label: string; path: string; icon: React.ReactNode }[] = [
    { id: 'dashboard', label: 'Dashboard', path: DASHBOARD_PATH, icon: <LayoutDashboard className="h-4 w-4" /> },
    {
      id: 'intelligence',
      label: 'TimeGlobe Intelligence',
      path: INTELLIGENCE_PATH,
      icon: <AiLogo className="h-4 w-4 shrink-0" />,
    },
  ];

  return (
    <div role="group" aria-label="Ansicht" className="flex items-center gap-1 rounded-lg bg-muted p-1">
      {options.map((option) => {
        const isActive = option.id === active;
        return (
          <Button
            key={option.id}
            variant={isActive ? 'default' : 'ghost'}
            size="sm"
            aria-current={isActive ? 'page' : undefined}
            onClick={() => {
              if (!isActive) navigate(option.path);
            }}
            className={cn(
              'gap-2 transition-all',
              isActive
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {option.icon}
            {option.label}
          </Button>
        );
      })}
    </div>
  );
};

export default AssistantModeSwitch;
